// src/pages/Marchands.js
// Page publique : annuaire des marchands avec logo + graduation (badge)
// Maxula

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiSearch, FiShoppingBag } from 'react-icons/fi';
import { merchantAPI } from '../services/api';
import useSeo from "../hooks/useSeo";
import { normalizeImagePath } from '../utils/imageUtils';
import MerchantBadge from '../components/common/MerchantBadge';

const logoUrl = (m) => {
  const raw =
    m?.logo ||
    m?.avatar ||
    m?.userId?.avatar ||
    m?.owner?.avatar ||
    null;
  if (!raw) return null;
  if (String(raw).includes('avatar-default')) return null;
  const p = normalizeImagePath(raw);
  return p || null;
};

const Marchands = () => {
  useSeo({ title: "Nos marchands partenaires | Maxula", description: "Découvrez tous les vendeurs et partenaires de Maxula : particuliers, promoteurs immobiliers et agences." });
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await merchantAPI.getAll({ status: 'active', limit: 200 });
        const list = res.data?.data || res.data?.merchants || [];
        setMerchants(Array.isArray(list) ? list : []);
      } catch (e) {
        // silencieux
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = merchants.filter((m) =>
    (m.businessName || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-extrabold text-gray-900">Nos marchands</h1>
        <p className="text-gray-500 mt-2">Découvrez les vendeurs partenaires de Maxula.</p>
      </div>

      <div className="max-w-md mx-auto mb-8 relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un marchand…"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 outline-none"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((m) => {
            const url = logoUrl(m);
            return (
              <Link
                to={`/marchands/${m.slug || m._id || m.id}`}
                key={m._id || m.id}
                className="group rounded-2xl border border-gray-200 bg-white p-5 flex items-center gap-4 hover:shadow-md hover:border-primary-300 transition"
              >
                <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                  {url ? (
                    <img src={url} alt={m.businessName} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-gray-400">
                      {(m.businessName || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary-600">
                      {m.businessName}
                    </h3>
                    {m.verified && <FiCheckCircle className="text-blue-500 shrink-0" title="Vérifié" />}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {{ merchant: 'Particulier / Marchand', promoteur: 'Promoteur immobilier', agence: 'Agence' }[m.businessType] || m.category}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <MerchantBadge tier={m.tier} />
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                      <FiShoppingBag size={12} /> {m.dealsCount || 0} offres
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center text-gray-500 py-12">Aucun marchand trouvé.</div>
      )}
    </div>
  );
};

export default Marchands;
